import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function readCurrentSubscription() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return { reg, sub };
}

async function persistSubscription(sub) {
  const payload = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
    expiration_time: payload.expirationTime || null,
    user_agent: navigator.userAgent,
  });
}

/** Manages the browser Push subscription for the current device. */
export function usePushSubscription() {
  const [status, setStatus] = useState("idle");
  const [permission, setPermission] = useState(
    isPushSupported() ? Notification.permission : "denied",
  );
  const [enabled, setEnabled] = useState(false);

  const refresh = useCallback(async () => {
    if (!isPushSupported()) return;
    try {
      const { sub } = await readCurrentSubscription();
      setEnabled(!!sub);
      setPermission(Notification.permission);
    } catch (err) {
      console.debug("[push] refresh failed:", err?.message || err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    if (!isPushSupported()) return;
    if (!VAPID_PUBLIC_KEY) {
      toast.error("Push notifications are not configured");
      return;
    }
    setStatus("enabling");
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notification permission was denied");
        setStatus("idle");
        return;
      }
      const { reg, sub: existing } = await readCurrentSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      await persistSubscription(sub);
      setEnabled(true);
      setStatus("enabled");
      toast.success("Notifications enabled — we'll ping you before every check-in");
    } catch (e) {
      toast.error(formatError(e) || "Could not enable notifications");
      setStatus("idle");
    }
  }, []);

  const disable = useCallback(async () => {
    setStatus("disabling");
    try {
      const { sub } = await readCurrentSubscription();
      if (sub) {
        try {
          await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } });
        } catch (err) {
          console.debug("[push] server unsubscribe failed (continuing local):", err?.message || err);
        }
        await sub.unsubscribe();
      }
      setEnabled(false);
      setStatus("idle");
      toast.success("Notifications disabled on this device");
    } catch (e) {
      toast.error(formatError(e));
      setStatus("idle");
    }
  }, []);

  const sendTest = useCallback(async () => {
    try {
      await api.post("/push/test");
      toast.success("Test notification sent — check your device");
    } catch (e) {
      toast.error(formatError(e));
    }
  }, []);

  return { status, permission, enabled, enable, disable, sendTest };
}
